/**
 * Which cities feed the "book nearby" rail on a hub that has nothing to book.
 *
 * The answer is always a list of real `cities` rows that hold at least one
 * bookable, non-transfer activity (`activitiesCount` from `/cities/explore`
 * already excludes transfers and explore rows). The order is decided in three
 * tiers, first match wins:
 *
 * 1. **Coordinates.** When the hub's own city row carries latitude and
 *    longitude, every other city is ranked by straight-line distance.
 * 2. **The drive-time table.** A hub can be for a place with no `cities` row
 *    at all (Merzouga, today), so there are no coordinates to measure from.
 *    `CategoryNearbyCities` already keeps a hand-checked table of which cities
 *    sit near which, and it knows Merzouga; its order is used as is.
 * 3. **Most to book.** With neither, the cities with the most bookable
 *    activities lead. Not "nearby", but never empty and never invented.
 *
 * Cities are then taken in that order until they hold enough activities to
 * fill the rail, so a near city with a single activity does not leave the rail
 * one card long.
 */
import { NEARBY_CITIES } from "./nearby-city-table";

export interface CityWithInventory {
  slug: string;
  name?: string;
  latitude: number | null;
  longitude: number | null;
  /** Published bookable activities, transfers excluded. */
  activitiesCount: number;
}

/** Old spellings of a city slug, kept so a "fes" hub still finds the "fez" row. */
const SLUG_ALIASES: Record<string, string> = { fes: "fez" };

function canonical(slug: string): string {
  return SLUG_ALIASES[slug] ?? slug;
}

function hasCoords(c: CityWithInventory): c is CityWithInventory & { latitude: number; longitude: number } {
  return (
    typeof c.latitude === "number" &&
    typeof c.longitude === "number" &&
    Number.isFinite(c.latitude) &&
    Number.isFinite(c.longitude)
  );
}

/** Great-circle distance in km. */
export function distanceKm(a: { latitude: number; longitude: number }, b: { latitude: number; longitude: number }): number {
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b.latitude - a.latitude);
  const dLon = rad(b.longitude - a.longitude);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(a.latitude)) * Math.cos(rad(b.latitude)) * Math.sin(dLon / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(h)));
}

export interface EmptyCityHubInput {
  city?: string;
  category?: string;
  total: number;
  q?: string;
  minPrice?: string | number;
  maxPrice?: string | number;
  show?: string;
}

/**
 * True when a city hub, or a city + category hub, has nothing in it at all.
 *
 * Shared by the page and the route loaders so the loader prefetches exactly
 * when the page will render the empty state. Stays is left out: its grid adds
 * every LiteAPI property and is never empty in the same sense. Anything that
 * narrows the list (a search, a price range, the audience filter) is left out
 * too, because an empty result there says nothing about the city.
 */
export function isEmptyCityHubView(input: EmptyCityHubInput): boolean {
  const set = (v: string | number | undefined) => v !== undefined && v !== "";
  return (
    !!input.city &&
    input.category !== "stays" &&
    input.total === 0 &&
    !input.q?.trim() &&
    !set(input.minPrice) &&
    !set(input.maxPrice) &&
    !input.show
  );
}

/** A list response's total, the same way the page reads it. */
export function listTotal(response: unknown): number {
  const r = (response ?? {}) as { total?: number; data?: unknown[] };
  return typeof r.total === "number" ? r.total : Array.isArray(r.data) ? r.data.length : 0;
}

export type NearbyStrategy = "coordinates" | "drive-table" | "inventory";

export interface NearbyPick {
  cities: CityWithInventory[];
  strategy: NearbyStrategy;
}

export interface PickNearbyOptions {
  /** How many activities the rail wants to show. */
  want?: number;
  /** Never read from more cities than this. */
  maxCities?: number;
  /**
   * Put the hub's own city first when it has inventory. True on a
   * city + category hub: nothing in that category, but the city itself may
   * well have other things to book.
   */
  includeSelf?: boolean;
}

export function pickNearbyBookableCities(
  all: CityWithInventory[],
  fromSlug: string,
  { want = 6, maxCities = 3, includeSelf = false }: PickNearbyOptions = {},
): NearbyPick {
  const from = canonical(fromSlug);
  const self = all.find((c) => c.slug === from);
  const others = all.filter((c) => c.slug !== from && c.activitiesCount > 0);

  let ranked: CityWithInventory[];
  let strategy: NearbyStrategy;
  const table = NEARBY_CITIES[fromSlug] ?? NEARBY_CITIES[from];

  if (self && hasCoords(self) && others.some(hasCoords)) {
    strategy = "coordinates";
    ranked = others
      .filter(hasCoords)
      .map((c) => ({ c, d: distanceKm(self, c) }))
      .sort((a, b) => a.d - b.d || a.c.slug.localeCompare(b.c.slug))
      .map(({ c }) => c);
  } else if (table) {
    const bySlug = new Map(others.map((c) => [c.slug, c]));
    ranked = table
      .map((edge) => bySlug.get(canonical(edge.slug)))
      .filter((c): c is CityWithInventory => !!c);
    strategy = "drive-table";
  } else {
    ranked = [];
    strategy = "inventory";
  }

  if (ranked.length === 0) {
    strategy = "inventory";
    ranked = others
      .slice()
      .sort((a, b) => b.activitiesCount - a.activitiesCount || a.slug.localeCompare(b.slug));
  }

  if (includeSelf && self && self.activitiesCount > 0) ranked = [self, ...ranked];

  const picked: CityWithInventory[] = [];
  let held = 0;
  for (const city of ranked) {
    if (picked.length >= maxCities || held >= want) break;
    picked.push(city);
    held += city.activitiesCount;
  }
  return { cities: picked, strategy };
}
